import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import { FaDownload, FaEdit, FaTrash, FaExchangeAlt, FaArrowLeft, FaEnvelope } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGuard from '../PermissionGuard';

const InvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/invoices/${id}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoice details');
      console.error('Error fetching invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`/invoices/${id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.type}-${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) {
      return;
    }

    try {
      await api.delete(`/invoices/${id}`);
      toast.success('Invoice deleted successfully');
      navigate('/invoices');
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleConvertProforma = async () => {
    try {
      const response = await api.post(`/invoices/${id}/convert`);
      toast.success('Proforma invoice converted successfully');
      navigate(`/invoices/${response.data._id}`);
    } catch (error) {
      toast.error('Failed to convert proforma invoice');
    }
  };

  const handleEmail = async () => {
    try {
      await api.post(`/invoices/${id}/email`);
      toast.success('Invoice email sent successfully');
    } catch (error) {
      toast.error('Failed to send invoice email');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      overdue: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-orange-100 text-orange-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Invoice Not Found</h3>
        <p className="text-gray-500">The invoice you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/invoices')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FaArrowLeft className="mr-2" />
            Back to Invoices
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">
            {invoice.type === 'invoice' ? 'Invoice' : 'Proforma Invoice'} - {invoice.invoiceNumber}
          </h1>
        </div>
        
        <div className="flex space-x-2">
          <PermissionGuard requiredPermission="View Invoice">
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FaDownload className="mr-2" />
              Download PDF
            </button>
          </PermissionGuard>
          
          <PermissionGuard requiredPermission="View Invoice">
            <button
              onClick={handleEmail}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FaEnvelope className="mr-2" />
              Send Email
            </button>
          </PermissionGuard>
          
          {invoice.type === 'proforma' && checkPermission('Create Invoice') && (
            <button
              onClick={handleConvertProforma}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
            >
              <FaExchangeAlt className="mr-2" />
              Convert to Invoice
            </button>
          )}
          
          <PermissionGuard requiredPermission="Update Invoice">
            <button
              onClick={() => navigate(`/invoices/${id}/edit`)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <FaEdit className="mr-2" />
              Edit
            </button>
          </PermissionGuard>
          
          <PermissionGuard requiredPermission="Delete Invoice">
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
            >
              <FaTrash className="mr-2" />
              Delete
            </button>
          </PermissionGuard>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Invoice Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Invoice Information */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Invoice Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Invoice Number</label>
                <p className="mt-1 text-sm text-gray-900">{invoice.invoiceNumber}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <p className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    invoice.type === 'invoice' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                  }`}>
                    {invoice.type === 'invoice' ? 'Invoice' : 'Proforma'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Issue Date</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(invoice.issueDate).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Due Date</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(invoice.dueDate).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <p className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                    {invoice.status}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Payment Status</label>
                <p className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusColor(invoice.paymentStatus)}`}>
                    {invoice.paymentStatus}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Billing Information */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Billing Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Bill To</h3>
                <div className="text-sm text-gray-900">
                  <p className="font-medium">{invoice.billingAddress?.name || 'N/A'}</p>
                  <p>{invoice.billingAddress?.address || 'N/A'}</p>
                  {invoice.billingAddress?.city && (
                    <p>{invoice.billingAddress.city}, {invoice.billingAddress.state || ''} {invoice.billingAddress.zipCode || ''}</p>
                  )}
                  {invoice.billingAddress?.email && <p>{invoice.billingAddress.email}</p>}
                  {invoice.billingAddress?.phone && <p>{invoice.billingAddress.phone}</p>}
                </div>
              </div>
              
              {invoice.formSubmission?.userId && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Form Submitted By</h3>
                  <div className="text-sm text-gray-900">
                    <p className="font-medium">
                      {invoice.formSubmission.userId.firstName} {invoice.formSubmission.userId.lastName}
                    </p>
                    <p>{invoice.formSubmission.userId.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Form Submission ID: {invoice.formSubmission._id}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Items */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Items</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tax Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(invoice.items || []).map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.description || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.quantity || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(item.unitPrice || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(item.taxRate || 0)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(item.totalAmount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Totals */}
            <div className="mt-6 flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">${(invoice.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-medium">${(invoice.taxTotal || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold border-t pt-2">
                  <span>Total:</span>
                  <span>${(invoice.totalAmount || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes and Terms */}
          {(invoice.notes || invoice.terms) && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h2>
              <div className="space-y-4">
                {invoice.notes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Notes</h3>
                    <p className="text-sm text-gray-900">{invoice.notes}</p>
                  </div>
                )}
                {invoice.terms && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Terms & Conditions</h3>
                    <p className="text-sm text-gray-900">{invoice.terms}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Conversion Information */}
          {(invoice.convertedFrom || invoice.convertedTo) && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Conversion Information</h2>
              <div className="space-y-3">
                {invoice.convertedFrom && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Converted From</label>
                    <p className="mt-1 text-sm text-gray-900">{invoice.convertedFrom.invoiceNumber || 'N/A'}</p>
                  </div>
                )}
                {invoice.convertedTo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Converted To</label>
                    <p className="mt-1 text-sm text-gray-900">{invoice.convertedTo.invoiceNumber || 'N/A'}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Paid Amount</label>
                <p className="mt-1 text-sm text-gray-900">${(invoice.paidAmount || 0).toFixed(2)}</p>
              </div>
              {invoice.paidDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Paid Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(invoice.paidDate).toLocaleDateString()}</p>
                </div>
              )}
            </div>
          </div>

          {/* Audit Information */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Audit Information</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Created By</label>
                <p className="mt-1 text-sm text-gray-900">{invoice.createdBy?.name || 'Unknown'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Created Date</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(invoice.createdAt).toLocaleDateString()}</p>
              </div>
              {invoice.updatedBy && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Updated By</label>
                  <p className="mt-1 text-sm text-gray-900">{invoice.updatedBy.name}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(invoice.updatedAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetails; 